/**
 * Loads colour-pattern entries past the first 250 and adds them to the
 * page-level `--swatch-<handle>` index. See snippets/swatch-index.liquid.
 *
 * Swatches for those colours were rendered with `data-swatch-handle` and
 * `var(--swatch-<handle>, <fallback>)`, so defining the property is enough
 * to paint them, including markup rendered later by AJAX. The rules below
 * only undo the "unknown colour" presentation for handles that loaded.
 * No swatch element is touched. If a request fails, the swatches keep
 * the fallback they were rendered with.
 */
(() => {
  const script = document.currentScript;
  if (!script || window.swatchIndexLoading) {
    return;
  }
  window.swatchIndexLoading = true;

  const style = document.createElement('style');
  style.dataset.swatchIndex = '';
  document.head.append(style);

  const addEntries = (entries) => {
    if (!entries.length) {
      return;
    }

    const properties = entries
      .map(({ handle, value, position }) => {
        let css = `--swatch-${handle}: ${value};`;
        if (position) {
          css +=`--swatch-${handle}-position: ${position};`;
        }
        return css;
      })
      .join('');
    const loaded = `:is(${entries.map(({ handle }) => `[data-swatch-handle="${handle}"]`).join(',')})`;

    style.append(
      `:root{${properties}}`,
      // Card / filter swatch (snippets/swatch.liquid).
      `.swatch.swatch--unavailable${loaded}{border-style:var(--style-border-swatch-style)}`,
      // Card swatch row hidden because no colour resolved on page 1 (blocks/swatches.liquid).
      `.product-swatches--pending:has(${loaded}){display:flex}`,
      // Group selector sibling swatch (blocks/product-group-selector.liquid): back to
      // the base .product-purchase-form__swatch size, without the text label.
      `.product-purchase-form__swatch--fallback${loaded}{width:44px;min-width:0;padding-inline:2px}`,
      `${loaded} .product-purchase-form__swatch-text{display:none}`
    );
  };

  const requested = new Set();

  const load = async (url) => {
    // A repeated `next` would otherwise loop and re-append the same rules.
    if (requested.has(url)) {
      return;
    }
    requested.add(url);
    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) {
        return;
      }
      const { entries = [], next } = await response.json();
      addEntries(entries);
      if (next) {
        load(next);
      }
    } catch (error) {
      // Keep the server-rendered fallbacks.
    }
  };

  if (script.dataset.swatchIndexUrl) {
    load(script.dataset.swatchIndexUrl);
  }
})();
